import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase-client";

export const REAUTH_REQUIRED_EVENT = "bdse:reauth-required";

const AUTH_TIMEOUT_MS = 8_000;
const EDGE_TIMEOUT_MS = 15_000;
const POSTGREST_TIMEOUT_MS = 12_000;
const REFRESH_WINDOW_MS = 90_000;

export class ReauthRequiredError extends Error {
  constructor(message = "Your session expired. Please sign in again.") {
    super(message);
    this.name = "ReauthRequiredError";
  }
}

export class SupabaseTimeoutError extends Error {
  constructor(label: string, timeoutMs: number) {
    super(`${label} timed out after ${Math.round(timeoutMs / 1000)}s. Try again.`);
    this.name = "SupabaseTimeoutError";
  }
}

export function requestReauth(reason: string): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(REAUTH_REQUIRED_EVENT, { detail: { reason } }),
  );
}

export async function withSupabaseTimeout<T>(
  operation: PromiseLike<T>,
  label: string,
  timeoutMs = EDGE_TIMEOUT_MS,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      Promise.resolve(operation),
      new Promise<T>((_resolve, reject) => {
        timer = setTimeout(
          () => reject(new SupabaseTimeoutError(label, timeoutMs)),
          timeoutMs,
        );
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function getSessionWithTimeout(): Promise<Session | null> {
  const { data, error } = await withSupabaseTimeout(
    supabase.auth.getSession(),
    "Session check",
    AUTH_TIMEOUT_MS,
  );
  if (error) {
    requestReauth("get-session-error");
    throw new ReauthRequiredError(error.message);
  }
  return data.session;
}

export async function ensureFreshSession(): Promise<Session> {
  const session = await getSessionWithTimeout();
  if (!session) {
    requestReauth("missing-session");
    throw new ReauthRequiredError();
  }

  const expiresAt = session.expires_at ? session.expires_at * 1000 : null;
  if (!expiresAt || expiresAt - Date.now() > REFRESH_WINDOW_MS) {
    return session;
  }

  const { data, error } = await withSupabaseTimeout(
    supabase.auth.refreshSession(),
    "Session refresh",
    AUTH_TIMEOUT_MS,
  );
  if (error || !data.session) {
    requestReauth("refresh-session-error");
    throw new ReauthRequiredError(error?.message);
  }
  return data.session;
}

export const SUPABASE_EDGE_TIMEOUT_MS = EDGE_TIMEOUT_MS;
export const SUPABASE_POSTGREST_TIMEOUT_MS = POSTGREST_TIMEOUT_MS;
